import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type {
  LeaseViewId,
  LeaseColumnGroupId,
  LeaseGroupingOption,
  LeaseStatus,
} from '@/types/leases'
import { getLeaseViewConfig, LEASE_COLUMN_GROUPS } from '@/types/leases'
import type { RecordStatusFilter } from '@/types/record-status'

// ============================================
// Types
// ============================================

export type DetailPanelMode = 'modal' | 'sidePanel'

interface LeaseViewState {
  activeView: LeaseViewId
  quickFilters: { status: LeaseStatus[] }
  groupBy: LeaseGroupingOption
  visibleColumnGroups: Set<LeaseColumnGroupId>
  search: string
  detailPanelMode: DetailPanelMode
  selectedLeaseId: string | null
  recordStatus: RecordStatusFilter
}

interface LeaseViewActions {
  setActiveView: (viewId: LeaseViewId) => void
  setStatusFilter: (values: LeaseStatus[]) => void
  resetFiltersToViewDefaults: () => void
  getIsModified: () => boolean
  setGroupBy: (option: LeaseGroupingOption) => void
  toggleColumnGroup: (groupId: LeaseColumnGroupId) => void
  setSearch: (search: string) => void
  isColumnGroupVisible: (groupId: LeaseColumnGroupId) => boolean
  setDetailPanelMode: (mode: DetailPanelMode) => void
  setSelectedLeaseId: (id: string | null) => void
  setRecordStatus: (status: RecordStatusFilter) => void
}

// ============================================
// Default values
// ============================================

function getDefaultColumnGroups(viewId: LeaseViewId): Set<LeaseColumnGroupId> {
  const config = getLeaseViewConfig(viewId)
  return new Set(config.defaultColumnGroups)
}

function getDefaultQuickFilters(viewId: LeaseViewId): { status: LeaseStatus[] } {
  const config = getLeaseViewConfig(viewId)
  return { status: [...config.defaultFilters.status] }
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((v, i) => v === sortedB[i])
}

// ============================================
// Store
// ============================================

export const useLeaseViewStore = create<LeaseViewState & LeaseViewActions>()(
  devtools(
    persist(
      (set, get) => ({
        activeView: 'active_portfolio',
        quickFilters: getDefaultQuickFilters('active_portfolio'),
        groupBy: 'none',
        visibleColumnGroups: getDefaultColumnGroups('active_portfolio'),
        search: '',
        detailPanelMode: 'sidePanel',
        selectedLeaseId: null,
        recordStatus: 'active' as RecordStatusFilter,

        setActiveView: (viewId) => {
          set({
            activeView: viewId,
            quickFilters: getDefaultQuickFilters(viewId),
            groupBy: 'none',
            visibleColumnGroups: getDefaultColumnGroups(viewId),
            search: '',
            selectedLeaseId: null,
            recordStatus: 'active' as RecordStatusFilter,
          })
        },

        setStatusFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, status: values },
          })),

        resetFiltersToViewDefaults: () => {
          const { activeView } = get()
          set({
            quickFilters: getDefaultQuickFilters(activeView),
            groupBy: 'none',
          })
        },

        getIsModified: () => {
          const { activeView, quickFilters } = get()
          const defaults = getLeaseViewConfig(activeView).defaultFilters
          return !arraysEqual(quickFilters.status, defaults.status)
        },

        setGroupBy: (option) => set({ groupBy: option }),

        toggleColumnGroup: (groupId) =>
          set((state) => {
            const group = LEASE_COLUMN_GROUPS.find((g) => g.id === groupId)
            if (group?.alwaysOn) return state

            const next = new Set(state.visibleColumnGroups)
            if (next.has(groupId)) {
              next.delete(groupId)
            } else {
              next.add(groupId)
            }
            return { visibleColumnGroups: next }
          }),

        setSearch: (search) => set({ search }),

        isColumnGroupVisible: (groupId) => {
          const group = LEASE_COLUMN_GROUPS.find((g) => g.id === groupId)
          if (group?.alwaysOn) return true
          return get().visibleColumnGroups.has(groupId)
        },

        setDetailPanelMode: (mode) => set({ detailPanelMode: mode }),

        setSelectedLeaseId: (id) => set({ selectedLeaseId: id }),

        setRecordStatus: (status) => set({ recordStatus: status }),
      }),
      {
        name: 'lease-view',
        partialize: (state) => ({
          activeView: state.activeView,
          groupBy: state.groupBy,
          visibleColumnGroups: Array.from(state.visibleColumnGroups),
          detailPanelMode: state.detailPanelMode,
        }),
        merge: (persisted, current) => {
          const p = persisted as {
            activeView?: LeaseViewId
            groupBy?: LeaseGroupingOption
            visibleColumnGroups?: LeaseColumnGroupId[]
            detailPanelMode?: DetailPanelMode
          }

          const validViews: LeaseViewId[] = ['active_portfolio', 'expiring', 'drafts', 'all']
          const activeView = p.activeView && validViews.includes(p.activeView)
            ? p.activeView
            : current.activeView

          return {
            ...current,
            activeView,
            groupBy: p.groupBy ?? current.groupBy,
            visibleColumnGroups: p.visibleColumnGroups
              ? new Set(p.visibleColumnGroups)
              : current.visibleColumnGroups,
            detailPanelMode: p.detailPanelMode ?? current.detailPanelMode,
            quickFilters: getDefaultQuickFilters(activeView),
          }
        },
      }
    ),
    { name: 'lease-view' }
  )
)
